import React, {  } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router';
import styled from 'styled-components/macro';

import Card from '../../../components/Card';
import { distances, colors } from '../../../styles/constants';
import { Table, Thead, Tr, Th, Tbody, Td } from '../../../components/Table';
import { formatDate } from '../../../helpers/formatters';
import { ActiveCampaigns } from '../helpers';
import { getRemainingTime } from '../../utils/getRemainingTime';
import { StyledTd, StyledTh, StyledTr } from '../common';

interface ActiveCampaignsListProps {
    campaigns: ActiveCampaigns[];
}

const ActiveCampaignsList = (
    {
        campaigns,
    }: ActiveCampaignsListProps
) => {
    const { t, i18n } = useTranslation();
    const { accountId } = useParams();
    const history = useHistory();

    const getBadgeText = (editedDate: string) =>
        `${t('marketing.campaign.edited', { date: formatDate(editedDate, i18n.language) })}`;

    const wasUpdated = (updatedAt: string, createdAt: string) => {
        if (updatedAt) {
            const updatedDate = new Date(updatedAt).getTime();
            const createdDate = new Date(createdAt).getTime();
            return updatedDate !== createdDate;
        }
        return false;
    };

    return (
        <Card
            title={t('marketing.campaign.active')}
            subtitle={t('marketing.campaign.active_campaigns_overview')}
            shortHr
            marginBottom={distances.small}
        >
            <Table>
                <Thead>
                    <Tr>
                        <StyledTh>{t('marketing.campaign.name')}</StyledTh>
                        <Th>{t('marketing.campaign.time_used')}</Th>
                        <Th>{t('marketing.campaign.discount_given')}</Th>
                        <Th>{t('marketing.campaign.started')}</Th>
                        <Th>{t('marketing.campaign.ends')}</Th>
                        <Th>{t('marketing.campaign.created')}</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {campaigns.map((campaign, index) => (
                        <StyledTr
                            key={index}
                            onClick={() => history.push(`/${accountId}/marketing/campaigns/${campaign.id}/edit`)}
                        >
                            <StyledTd>
                                {campaign.name}{' '}
                                {wasUpdated(campaign.updatedAt, campaign.createdAt) && (
                                    <Badge>{getBadgeText(campaign.updatedAt)}</Badge>
                                )}
                            </StyledTd>
                            <Td>{campaign.timeUsed}</Td>
                            <Td>{campaign.discountGiven}</Td>
                            <Td>{formatDate(campaign.started, i18n.language)}</Td>
                            <Td>{getRemainingTime(campaign.ends, i18n)}</Td>
                            <Td>{formatDate(campaign.createdAt, i18n.language)}</Td>
                        </StyledTr>
                    ))}
                </Tbody>
            </Table>
        </Card>
    );
};

const Badge = styled.span`
    display: inline-block;
    background: rgb(2, 214, 130, 0.2);
    border-radius: 2px;
    font-size: 11px;
    color: ${colors.secondary.child};
    text-align: center;
    line-height: 16px;
    padding: 3px 8px;
    font-weight: 700;
    margin-left: ${distances.small};

    & + & {
        margin-left: ${distances.tiny};
    }
`;

export default ActiveCampaignsList;
